import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authenticationApi, sessionStorageService } from "../authentication";

const FavoritesContext = createContext({
    favoriteIds: new Set(),
    isFavorited: () => false,
    toggleFavorite: async () => {},
});

/**
 * Single source of truth for "saved properties" so the home page grid
 * (PropertyCard) and the AI chat results (AiPropertyCard) show the same
 * favorited state instead of each keeping its own dead, local-only toggle.
 */
export function FavoritesProvider({ isAuthenticated, children }) {
    const [favoriteIds, setFavoriteIds] = useState(new Set());

    useEffect(() => {
        if (!isAuthenticated) {
            setFavoriteIds(new Set());
            return;
        }
        authenticationApi.listFavorites()
            .then((list) => setFavoriteIds(new Set((list || []).map((p) => p.id))))
            .catch(() => {});
    }, [isAuthenticated]);

    const toggleFavorite = useCallback(async (propertyId) => {
        if (!sessionStorageService.get()) return false;

        const wasFavorited = favoriteIds.has(propertyId);
        setFavoriteIds((prev) => {
            const next = new Set(prev);
            if (wasFavorited) next.delete(propertyId); else next.add(propertyId);
            return next;
        });

        try {
            if (wasFavorited) await authenticationApi.removeFavorite(propertyId);
            else await authenticationApi.addFavorite(propertyId);
            return true;
        } catch {
            // revert the optimistic update on failure
            setFavoriteIds((prev) => {
                const next = new Set(prev);
                if (wasFavorited) next.add(propertyId); else next.delete(propertyId);
                return next;
            });
            return false;
        }
    }, [favoriteIds]);

    const isFavorited = useCallback((id) => favoriteIds.has(id), [favoriteIds]);

    return (
        <FavoritesContext.Provider value={{ favoriteIds, isFavorited, toggleFavorite }}>
            {children}
        </FavoritesContext.Provider>
    );
}

export function useFavorites() {
    return useContext(FavoritesContext);
}
